import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormattedNumberInput } from "@/components/ui/formatted-number-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useCallback, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, getCsrfHeaders } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useVerificationStatus } from "@/components/VerificationBanner";
import { Package, Box, Layers, AlertTriangle, Loader2, Plus, Trash2, Mail, Users, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { EmailVerificationModal } from "@/components/EmailVerificationModal";
import { UpgradeModal } from "@/components/UpgradeModal";


const HANDLING_UNITS = ["Pallet", "Crate", "Box", "Other"];

interface EmailCheckResult {
  exists: boolean;
  verified?: boolean;
  firstName?: string;
  lastName?: string;
  company?: string;
  userType?: string;
}

interface FreightItem {
  id: string;
  handlingUnit: string;
  length: string;
  width: string;
  height: string;
  weight: string;
  quantity: string;
  stackable: boolean;
  hazardous: boolean;
}

function createEmptyItem(): FreightItem {
  return {
    id: crypto.randomUUID(),
    handlingUnit: "Pallet",
    length: "",
    width: "",
    height: "",
    weight: "",
    quantity: "1",
    stackable: false,
    hazardous: false,
  };
}

const LOCATION_TYPES = [
  { value: "Business", label: "Business (with loading dock or forklift)" },
  { value: "BusinessNoDoc", label: "Business (without loading dock)" },
  { value: "Residential", label: "Residential" },
  { value: "Construction", label: "Construction Site" },
  { value: "TradeShow", label: "Trade Show/Convention" },
  { value: "Storage", label: "Storage Facility" },
];

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

async function lookupZipCode(zip: string): Promise<{ city: string; state: string } | null> {
  if (!/^\d{5}$/.test(zip)) return null;
  
  try {
    const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
    if (!response.ok) return null;
    
    const data = await response.json();
    if (data.places && data.places.length > 0) {
      return {
        city: data.places[0]["place name"],
        state: data.places[0]["state abbreviation"],
      };
    }
    return null;
  } catch (error) {
    console.error("Zip code lookup failed:", error);
    return null;
  }
}

export default function PostLoad() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { canPost, isLoading: verificationLoading } = useVerificationStatus();
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [postLimitModalOpen, setPostLimitModalOpen] = useState(false);
  const [postLimitReason, setPostLimitReason] = useState<"FREE_TIER_LIMIT_REACHED" | "BROKER_UPGRADE_REQUIRED">("FREE_TIER_LIMIT_REACHED");

  useEffect(() => {
    if (user && user.emailVerified === false) {
      setShowEmailVerificationModal(true);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const tier = user.membershipTier ?? "Free";
    if (user.userType === "Broker" && tier !== "Paid" && tier !== "Annual") {
      setPostLimitReason("BROKER_UPGRADE_REQUIRED");
      setPostLimitModalOpen(true);
    }
  }, [user]);

  const [pickupZipLoading, setPickupZipLoading] = useState(false);
  const [deliveryZipLoading, setDeliveryZipLoading] = useState(false);
  
  // Freight items - multiple items per load
  const [freightItems, setFreightItems] = useState<FreightItem[]>([createEmptyItem()]);
  
  const [formData, setFormData] = useState({
    // Pickup location fields
    pickupZip: "",
    pickupCity: "",
    pickupState: "",
    // Delivery location fields
    deliveryZip: "",
    deliveryCity: "",
    deliveryState: "",
    // Other fields
    pickupDate: "",
    deliveryDate: "",
    details: "",
    // Location Types
    pickupLocationType: "",
    deliveryLocationType: "",
    // Shipping Services - Pickup
    liftgatePickup: false,
    insidePickup: false,
    // Shipping Services - Delivery
    liftgateDelivery: false,
    insideDelivery: false,
    deliveryAppointmentRequired: false,
    callBeforeDelivery: false,
  });
  
  const isShipper = user?.userType === "Shipper";

  // Step state — only used for shippers
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [selectedPartnerEmails, setSelectedPartnerEmails] = useState<string[]>([]);
  const [skipNotifications, setSkipNotifications] = useState(false);

  // Partners query — only fetch for shippers on step 2
  const { data: partnersData } = useQuery<{ partners: any[] }>({
    queryKey: ["/api/preferred-partners"],
    enabled: isShipper && currentStep === 2,
  });
  const activePartners = (partnersData as any)?.partners ?? partnersData ?? [];

  const [inlinePartnerName, setInlinePartnerName] = useState("");
  const [inlinePartnerEmail, setInlinePartnerEmail] = useState("");
  const [inlinePartnerType, setInlinePartnerType] = useState("");
  const [inlineEmailCheck, setInlineEmailCheck] = useState<EmailCheckResult | null>(null);
  const [inlinePartnerLoading, setInlinePartnerLoading] = useState(false);
  const [inlinePartnerError, setInlinePartnerError] = useState<string | null>(null);

  const updateFreightItem = useCallback((id: string, updates: Partial<FreightItem>) => {
    setFreightItems(prev => prev.map(item => 
      item.id === id ? { ...item, ...updates } : item
    ));
  }, []);
  
  const addFreightItem = useCallback(() => {
    setFreightItems(prev => [...prev, createEmptyItem()]);
  }, []);
  
  const removeFreightItem = useCallback((id: string) => {
    setFreightItems(prev => prev.length > 1 ? prev.filter(item => item.id !== id) : prev);
  }, []);

  const handlePickupZipChange = useCallback(async (zip: string) => {
    setFormData(prev => ({ ...prev, pickupZip: zip }));
    
    if (zip.length === 5) {
      setPickupZipLoading(true);
      const result = await lookupZipCode(zip);
      setPickupZipLoading(false);
      
      if (result) {
        setFormData(prev => ({
          ...prev,
          pickupCity: result.city,
          pickupState: result.state,
        }));
      }
    }
  }, []);

  const handleDeliveryZipChange = useCallback(async (zip: string) => {
    setFormData(prev => ({ ...prev, deliveryZip: zip }));
    
    if (zip.length === 5) {
      setDeliveryZipLoading(true);
      const result = await lookupZipCode(zip);
      setDeliveryZipLoading(false);
      
      if (result) {
        setFormData(prev => ({
          ...prev,
          deliveryCity: result.city,
          deliveryState: result.state,
        }));
      }
    }
  }, []);

  const totalWeight = freightItems.reduce((sum, item) => {
    const weight = parseInt(item.weight) || 0;
    const quantity = parseInt(item.quantity) || 1;
    return sum + (weight * quantity);
  }, 0);

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch("/api/loads", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          throw new Error("Failed to post load");
        }
        throw new Error(errorData.message || errorData.error || "Failed to post load");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Load posted successfully!",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/loads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      setLocation("/dashboard");
    },
    onError: (error: any) => {
      const errorMessage = error.message || "";
      if (errorMessage.includes("BROKER_UPGRADE_REQUIRED")) {
        setPostLimitReason("BROKER_UPGRADE_REQUIRED");
        setPostLimitModalOpen(true);
      } else if (errorMessage.includes("FREE_TIER_LIMIT_REACHED")) {
        setPostLimitReason("FREE_TIER_LIMIT_REACHED");
        setPostLimitModalOpen(true);
      } else {
        toast({
          title: "Error posting load",
          description: errorMessage,
          variant: "destructive",
        });
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For shippers on step 1, advance to step 2 instead of submitting
    if (isShipper && currentStep === 1) {
      if (!formData.pickupZip || !formData.pickupCity || !formData.pickupState ||
          !formData.deliveryZip || !formData.deliveryCity || !formData.deliveryState ||
          !formData.pickupDate) {
        toast({ title: "Required fields missing", description: "Please fill in pickup and delivery details before continuing.", variant: "destructive" });
        return;
      }
      setCurrentStep(2);
      return;
    }

    // Step 2 submit (shippers) or direct submit (non-shippers) — build payload and mutate
    const firstItem = freightItems[0];
    const totalWeightCalc = freightItems.reduce((sum, item) => sum + (parseFloat(String(item.weight)) || 0), 0);
    const origin = `${formData.pickupCity}, ${formData.pickupState} ${formData.pickupZip}`;
    const destination = `${formData.deliveryCity}, ${formData.deliveryState} ${formData.deliveryZip}`;

    mutation.mutate({
      origin,
      destination,
      weight: totalWeightCalc,
      dimensions: `${firstItem.length} x ${firstItem.width} x ${firstItem.height}`,
      pickupDate: new Date(formData.pickupDate).toISOString(),
      deliveryDate: formData.deliveryDate ? new Date(formData.deliveryDate).toISOString() : null,
      details: formData.details,
      status: "Available",
      handlingUnit: firstItem.handlingUnit,
      length: firstItem.length ? parseInt(firstItem.length) : null,
      width: firstItem.width ? parseInt(firstItem.width) : null,
      height: firstItem.height ? parseInt(firstItem.height) : null,
      quantity: parseInt(firstItem.quantity) || 1,
      stackable: firstItem.stackable,
      hazardous: firstItem.hazardous,
      pickupLocationType: formData.pickupLocationType,
      deliveryLocationType: formData.deliveryLocationType,
      liftgatePickup: formData.liftgatePickup,
      insidePickup: formData.insidePickup,
      liftgateDelivery: formData.liftgateDelivery,
      insideDelivery: formData.insideDelivery,
      deliveryAppointmentRequired: formData.deliveryAppointmentRequired,
      callBeforeDelivery: formData.callBeforeDelivery,
      items: freightItems.map(item => ({
        handlingUnit: item.handlingUnit,
        length: item.length ? parseInt(item.length) : null,
        width: item.width ? parseInt(item.width) : null,
        height: item.height ? parseInt(item.height) : null,
        weight: parseInt(item.weight) || 0,
        quantity: parseInt(item.quantity) || 1,
        stackable: item.stackable,
        hazardous: item.hazardous,
      })),
      // Partner notifications — only sent if shipper didn't opt out
      notifyPartners: (isShipper && !skipNotifications) ? selectedPartnerEmails : [],
    });
  };

  const handleBackToStep1 = () => {
    setCurrentStep(1);
  };

  const handleInlineEmailCheck = async (email: string) => {
    if (!email || !email.includes("@")) { setInlineEmailCheck(null); return; }
    try {
      const res = await fetch(`/api/users/check-email?email=${encodeURIComponent(email)}`);
      const data = await res.json();
      setInlineEmailCheck(data);
    } catch { setInlineEmailCheck(null); }
  };

  const handleInlineAddPartner = async () => {
    setInlinePartnerError(null);
    if (!inlinePartnerName.trim() || !inlinePartnerEmail.trim() || !inlinePartnerType) {
      setInlinePartnerError("Please fill in name, email, and type.");
      return;
    }
    setInlinePartnerLoading(true);
    try {
      const res = await fetch("/api/partner-invites", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify({
          name: inlinePartnerName.trim(),
          email: inlinePartnerEmail.trim(),
          partnerType: inlinePartnerType,
        }),
      });
      const data = await res.json();
      if (data.status === "added") {
        setSelectedPartnerEmails(prev => [...prev, inlinePartnerEmail.trim().toLowerCase()]);
        queryClient.invalidateQueries({ queryKey: ["/api/preferred-partners"] });
      } else {
        setSelectedPartnerEmails(prev => [...prev, inlinePartnerEmail.trim().toLowerCase()]);
      }
      setInlinePartnerName("");
      setInlinePartnerEmail("");
      setInlinePartnerType("");
      setInlineEmailCheck(null);
    } catch {
      setInlinePartnerError("Failed to add partner. Please try again.");
    } finally {
      setInlinePartnerLoading(false);
    }
  };

  if (verificationLoading) return null;

  if (user && user.emailVerified === false) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <EmailVerificationModal
          isOpen={showEmailVerificationModal}
          onClose={() => setShowEmailVerificationModal(false)}
        />
        <h1 className="text-3xl font-bold mb-6" data-testid="text-post-load-title">Post a Load</h1>
        <Card className="p-12 text-center">
          <Mail className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
          <p className="font-medium text-muted-foreground" data-testid="text-email-verification-required">Email verification required</p>
          <p className="text-base md:text-sm text-muted-foreground mt-1">Verify your email address to post loads.</p>
          <Button
            className="mt-4"
            onClick={() => setShowEmailVerificationModal(true)}
            data-testid="button-verify-email-prompt"
          >
            Verify Email
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Post a Load</h1>

      {isShipper && (
        <div className="flex items-center gap-3 mb-4">
          <div className={`flex items-center gap-2 text-sm font-medium ${currentStep === 1 ? "text-primary" : "text-muted-foreground"}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${currentStep === 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>1</div>
            Shipment Details
          </div>
          <div className="h-px flex-1 bg-border" />
          <div className={`flex items-center gap-2 text-sm font-medium ${currentStep === 2 ? "text-primary" : "text-muted-foreground"}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${currentStep === 2 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>2</div>
            Notify Partners
          </div>
        </div>
      )}

      <Card className="p-8">
        <form onSubmit={handleSubmit} className="space-y-6">

        {isShipper && currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-1">Notify your partners</h2>
              <p className="text-sm text-muted-foreground">
                Select partners to notify about this load. They'll receive an email with the details and a link to submit their bid.
              </p>
            </div>

            {activePartners.length > 0 && (
              <div className="space-y-2">
                {activePartners.map((partner: any) => (
                  <label
                    key={partner.id}
                    className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50"
                  >
                    <Checkbox
                      checked={selectedPartnerEmails.includes(partner.email)}
                      onCheckedChange={(checked) => {
                        setSelectedPartnerEmails(prev =>
                          checked
                            ? [...prev, partner.email]
                            : prev.filter(e => e !== partner.email)
                        );
                      }}
                      data-testid={`checkbox-partner-${partner.id}`}
                    />
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {partner.company?.charAt(0) ?? partner.firstName?.charAt(0) ?? "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{partner.firstName} {partner.lastName}</p>
                      <p className="text-xs text-muted-foreground truncate">{partner.company} · {partner.userType}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {/* Always-visible inline add partner section */}
            <div className="border rounded-lg p-4 bg-muted/30">
              <p className="text-sm font-medium mb-3">
                {activePartners.length === 0 ? "Add a partner to notify" : "Notify someone new"}
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 mb-2">
                <Input
                  placeholder="Full name"
                  value={inlinePartnerName}
                  onChange={(e) => setInlinePartnerName(e.target.value)}
                  data-testid="input-inline-partner-name"
                />
                <Select value={inlinePartnerType} onValueChange={setInlinePartnerType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Broker or Carrier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="broker">Broker</SelectItem>
                    <SelectItem value="carrier">Carrier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Email address"
                  value={inlinePartnerEmail}
                  onChange={(e) => {
                    setInlinePartnerEmail(e.target.value);
                    setInlineEmailCheck(null);
                  }}
                  onBlur={(e) => handleInlineEmailCheck(e.target.value)}
                  data-testid="input-inline-partner-email"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleInlineAddPartner}
                  disabled={inlinePartnerLoading}
                  data-testid="button-inline-add-partner"
                >
                  {inlinePartnerLoading ? "Adding..." : "Add"}
                </Button>
              </div>

              {inlineEmailCheck && (
                <div className="mt-2 flex items-start gap-2">
                  {inlineEmailCheck.exists ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-green-700">
                        <strong>{inlineEmailCheck.firstName} {inlineEmailCheck.lastName}</strong>
                        {inlineEmailCheck.company ? ` — ${inlineEmailCheck.company}` : ""} · Already on Fill The Trucks
                      </p>
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-700">
                        <strong>{inlinePartnerEmail}</strong> — Invite to Fill The Trucks
                        <br />
                        <span className="text-muted-foreground">We'll send a free invite to join and bid on your load.</span>
                      </p>
                    </>
                  )}
                </div>
              )}
              {inlinePartnerError && (
                <p className="text-sm text-destructive mt-2">{inlinePartnerError}</p>
              )}
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={skipNotifications}
                onCheckedChange={(checked) => {
                  setSkipNotifications(checked as boolean);
                  if (checked) setSelectedPartnerEmails([]);
                }}
                data-testid="checkbox-skip-notifications"
              />
              <div>
                <p className="text-sm font-medium">Post to public board only</p>
                <p className="text-xs text-muted-foreground">Don't notify any partners — your load will still appear on the public board.</p>
              </div>
            </label>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleBackToStep1}
                className="flex-1"
                data-testid="button-back-to-step1"
              >
                Back
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={mutation.isPending}
                data-testid="button-post-load-submit"
              >
                {mutation.isPending ? "Posting..." : selectedPartnerEmails.length > 0 ? `Post & Notify ${selectedPartnerEmails.length} Partner${selectedPartnerEmails.length > 1 ? "s" : ""}` : "Post Load"}
              </Button>
            </div>
          </div>
        )}
        {(!isShipper || currentStep === 1) && (<>

          {/* ── Freight Information ── */}
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg">Freight Information</h3>
              {totalWeight > 0 && (
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Total Weight ({freightItems.length} item{freightItems.length > 1 ? 's' : ''}):</p>
                  <p className="text-sm font-bold" data-testid="text-total-weight">{totalWeight.toLocaleString()} lbs</p>
                </div>
              )}
            </div>

            {freightItems.map((item, index) => (
              <div
                key={item.id}
                className={index > 0 ? 'mt-5 pt-5 border-t' : ''}
                data-testid={`freight-item-${index}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-muted-foreground">
                    Item {index + 1}
                  </span>
                  {freightItems.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFreightItem(item.id)}
                      className="text-destructive"
                      data-testid={`button-remove-item-${index}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  )}
                </div>

                {/* Handling Unit — compact segmented control */}
                <div className="mb-4">
                  <Label className="block mb-2">Handling Unit</Label>
                  <div className="inline-flex">
                    {HANDLING_UNITS.map((unit, i) => (
                      <button
                        key={unit}
                        type="button"
                        onClick={() => updateFreightItem(item.id, { handlingUnit: unit })}
                        className={[
                          "px-3 py-1.5 text-xs font-medium border transition-colors",
                          i === 0 ? "rounded-l-md" : "",
                          i === HANDLING_UNITS.length - 1 ? "rounded-r-md" : "",
                          i > 0 ? "-ml-px" : "",
                          item.handlingUnit === unit
                            ? "bg-primary text-primary-foreground border-primary relative z-10"
                            : "bg-background text-foreground border-border hover:bg-muted"
                        ].join(" ")}
                        data-testid={`button-unit-${unit.toLowerCase()}-${index}`}
                      >
                        {unit}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Dimensions and Weight */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
                  <div className="space-y-1">
                    <Label>Length (in) *</Label>
                    <FormattedNumberInput 
                      decimals={0}
                      value={item.length}
                      onChange={(val) => updateFreightItem(item.id, { length: val })}
                      data-testid={`input-length-${index}`}
                      required={index === 0}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Width (in) *</Label>
                    <FormattedNumberInput 
                      decimals={0}
                      value={item.width}
                      onChange={(val) => updateFreightItem(item.id, { width: val })}
                      data-testid={`input-width-${index}`}
                      required={index === 0}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Height (in) *</Label>
                    <FormattedNumberInput 
                      decimals={0}
                      value={item.height}
                      onChange={(val) => updateFreightItem(item.id, { height: val })}
                      data-testid={`input-height-${index}`}
                      required={index === 0}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Weight (lbs) *</Label>
                    <FormattedNumberInput 
                      decimals={0}
                      value={item.weight}
                      onChange={(val) => updateFreightItem(item.id, { weight: val })}
                      data-testid={`input-weight-${index}`}
                      required={index === 0}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Quantity</Label>
                    <FormattedNumberInput 
                      decimals={0}
                      placeholder="1"
                      value={item.quantity}
                      onChange={(val) => updateFreightItem(item.id, { quantity: val })}
                      data-testid={`input-quantity-${index}`}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id={`stackable-${item.id}`}
                      checked={item.stackable}
                      onCheckedChange={(checked) => updateFreightItem(item.id, { stackable: checked === true })}
                      data-testid={`checkbox-stackable-${index}`}
                    />
                    <Label htmlFor={`stackable-${item.id}`} className="text-sm">Stackable</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id={`hazardous-${item.id}`}
                      checked={item.hazardous}
                      onCheckedChange={(checked) => updateFreightItem(item.id, { hazardous: checked === true })}
                      data-testid={`checkbox-hazardous-${index}`}
                    />
                    <Label htmlFor={`hazardous-${item.id}`} className="text-sm flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 text-yellow-500" />
                      Hazardous
                    </Label>
                  </div>
                </div>
              </div>
            ))}

            <div className="mt-5 pt-4 border-t">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addFreightItem}
                data-testid="button-add-item"
                className="text-muted-foreground"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Another Item
              </Button>
            </div>
          </div>

          {/* ── Pickup Information ── */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Pickup Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div className="space-y-2">
                <Label htmlFor="pickupDate">Pickup Date *</Label>
                <Input 
                  id="pickupDate"
                  type="date"
                  value={formData.pickupDate}
                  onChange={(e) => setFormData({...formData, pickupDate: e.target.value})}
                  data-testid="input-pickup-date"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pickupZip">Zip Code *</Label>
                <div className="relative">
                  <Input 
                    id="pickupZip"
                    maxLength={5}
                    value={formData.pickupZip}
                    onChange={(e) => handlePickupZipChange(e.target.value.replace(/\D/g, ''))}
                    data-testid="input-pickup-zip"
                    required
                  />
                  {pickupZipLoading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pickupCity">City *</Label>
                <Input 
                  id="pickupCity"
                  value={formData.pickupCity}
                  onChange={(e) => setFormData({...formData, pickupCity: e.target.value})}
                  data-testid="input-pickup-city"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pickupState">State *</Label>
                <Select 
                  value={formData.pickupState} 
                  onValueChange={(value) => setFormData({...formData, pickupState: value})}
                >
                  <SelectTrigger data-testid="select-pickup-state">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pickupLocationType">Location Type</Label>
                <Select 
                  value={formData.pickupLocationType} 
                  onValueChange={(value) => setFormData({...formData, pickupLocationType: value})}
                >
                  <SelectTrigger data-testid="select-pickup-location-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATION_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* ── Delivery Information ── */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Delivery Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div className="space-y-2">
                <Label htmlFor="deliveryDate">Delivery Date</Label>
                <Input 
                  id="deliveryDate"
                  type="date"
                  value={formData.deliveryDate}
                  onChange={(e) => setFormData({...formData, deliveryDate: e.target.value})}
                  data-testid="input-delivery-date"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="deliveryZip">Zip Code *</Label>
                <div className="relative">
                  <Input 
                    id="deliveryZip"
                    maxLength={5}
                    value={formData.deliveryZip}
                    onChange={(e) => handleDeliveryZipChange(e.target.value.replace(/\D/g, ''))}
                    data-testid="input-delivery-zip"
                    required
                  />
                  {deliveryZipLoading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliveryCity">City *</Label>
                <Input 
                  id="deliveryCity"
                  value={formData.deliveryCity}
                  onChange={(e) => setFormData({...formData, deliveryCity: e.target.value})}
                  data-testid="input-delivery-city"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliveryState">State *</Label>
                <Select 
                  value={formData.deliveryState} 
                  onValueChange={(value) => setFormData({...formData, deliveryState: value})}
                >
                  <SelectTrigger data-testid="select-delivery-state">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliveryLocationType">Location Type</Label>
                <Select 
                  value={formData.deliveryLocationType} 
                  onValueChange={(value) => setFormData({...formData, deliveryLocationType: value})}
                >
                  <SelectTrigger data-testid="select-delivery-location-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATION_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* ── Shipping Services ── */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Shipping Services</h3>
            <p className="text-base md:text-sm text-muted-foreground mb-4">
              For the most accurate quote, select all of the services needed to complete this shipment.
            </p>

            <div className="rounded-md p-3 mb-2 bg-muted">
              <Label className="block mb-3 text-sm font-medium text-foreground">Pickup Services</Label>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="liftgatePickup"
                    checked={formData.liftgatePickup}
                    onCheckedChange={(checked) => setFormData({...formData, liftgatePickup: checked === true})}
                    data-testid="checkbox-liftgate-pickup"
                  />
                  <Label htmlFor="liftgatePickup">Liftgate Service at Pickup</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="insidePickup"
                    checked={formData.insidePickup}
                    onCheckedChange={(checked) => setFormData({...formData, insidePickup: checked === true})}
                    data-testid="checkbox-inside-pickup"
                  />
                  <Label htmlFor="insidePickup">Inside Pickup</Label>
                </div>
              </div>
            </div>

            <div className="rounded-md p-3 bg-muted">
              <Label className="block mb-3 text-sm font-medium text-foreground">Delivery Services</Label>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="liftgateDelivery"
                    checked={formData.liftgateDelivery}
                    onCheckedChange={(checked) => setFormData({...formData, liftgateDelivery: checked === true})}
                    data-testid="checkbox-liftgate-delivery"
                  />
                  <Label htmlFor="liftgateDelivery">Liftgate Service at Delivery</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="insideDelivery"
                    checked={formData.insideDelivery}
                    onCheckedChange={(checked) => setFormData({...formData, insideDelivery: checked === true})}
                    data-testid="checkbox-inside-delivery"
                  />
                  <Label htmlFor="insideDelivery">Inside Delivery</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="deliveryAppointmentRequired"
                    checked={formData.deliveryAppointmentRequired}
                    onCheckedChange={(checked) => setFormData({...formData, deliveryAppointmentRequired: checked === true})}
                    data-testid="checkbox-delivery-appointment"
                  />
                  <Label htmlFor="deliveryAppointmentRequired">Delivery Appt. Required</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="callBeforeDelivery"
                    checked={formData.callBeforeDelivery}
                    onCheckedChange={(checked) => setFormData({...formData, callBeforeDelivery: checked === true})}
                    data-testid="checkbox-call-before-delivery"
                  />
                  <Label htmlFor="callBeforeDelivery">Call before Delivery</Label>
                </div>
              </div>
            </div>
          </div>

          {/* ── Details ── */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Details (Optional)</h3>

            <div className="space-y-2">
              <Label htmlFor="details">Additional Notes</Label>
              <Textarea 
                id="details"
                placeholder="Any special handling instructions, loading requirements, or additional information for carriers..."
                value={formData.details}
                onChange={(e) => setFormData({...formData, details: e.target.value})}
                data-testid="textarea-description"
                rows={4}
              />
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4">
            <Button 
              type="submit" 
              size="lg" 
              className="flex-1" 
              data-testid="button-post-load"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Posting..." : isShipper ? "Continue" : "Post Load"}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              size="lg"
              onClick={() => setLocation("/dashboard")}
              data-testid="button-cancel"
            >
              Cancel
            </Button>
          </div>

        </>)}

        </form>
      </Card>


      <UpgradeModal
        open={postLimitModalOpen}
        onClose={() => setPostLimitModalOpen(false)}
        reason={postLimitReason}
        userType={user?.userType ?? undefined}
      />
    </div>
  );
}
